import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, User, Phone, Mail, MapPin, CreditCard, Briefcase, FileText, ArrowUpCircle,
  ArrowDownCircle, ArrowLeftRight, AlertCircle, CheckCircle2, Clock, Banknote, Download, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { savingsAccountsApi, clientsApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { NewClientModal } from './ClientsPage';

const ClientLookupPage = ({ showNewClient = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [multipleResults, setMultipleResults] = useState([]);
  const [showTransaction, setShowTransaction] = useState(null);
  const [txAccountId, setTxAccountId] = useState('');
  const [txAmount, setTxAmount] = useState('');
  const [txLoading, setTxLoading] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferData, setTransferData] = useState({ to_account_id: '', amount: '', description: '' });
  const [transferLoading, setTransferLoading] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const inputRef = useRef(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setResult(null);
    setMultipleResults([]);
    try {
      const res = await api.get(`/clients/lookup?q=${encodeURIComponent(query.trim())}`);
      if (res.data.match === 'single') {
        setResult(res.data);
      } else if (res.data.match === 'multiple') {
        setMultipleResults(res.data.clients);
      } else {
        toast.error('No client found. Try account number, ID number, or name.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed');
    }
    setSearching(false);
  };

  const selectClient = async (clientId) => {
    setSearching(true);
    try {
      const res = await api.get(`/clients/lookup?q=${clientId}`);
      if (res.data.match === 'single') {
        setResult(res.data);
        setMultipleResults([]);
      }
    } catch (e) {
      toast.error('Failed to load client');
    }
    setSearching(false);
  };

  const handleTransaction = async (type) => {
    if (!txAccountId || !txAmount) return;
    setTxLoading(true);
    try {
      if (type === 'deposit') {
        await savingsAccountsApi.deposit(txAccountId, { amount: parseFloat(txAmount) });
      } else {
        await savingsAccountsApi.withdraw(txAccountId, { amount: parseFloat(txAmount) });
      }
      toast.success(`${type === 'deposit' ? 'Deposit' : 'Withdrawal'} of ${formatCurrency(parseFloat(txAmount))} successful`);
      setShowTransaction(null);
      setTxAmount('');
      // Refresh client data
      handleSearch();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${type} failed`);
    }
    setTxLoading(false);
  };

  const openTransfer = async (fromAccountId) => {
    setTxAccountId(fromAccountId);
    setShowTransfer(true);
    try {
      const res = await savingsAccountsApi.list({ limit: 100 });
      setAllAccounts(res.data.accounts.filter(a => a.id !== fromAccountId && a.status === 'active'));
    } catch (e) {}
  };

  const handleTransfer = async () => {
    setTransferLoading(true);
    try {
      const res = await api.post('/transfers/internal', {
        from_account_id: txAccountId,
        to_account_id: transferData.to_account_id,
        amount: parseFloat(transferData.amount),
        description: transferData.description || 'Internal transfer',
      });
      toast.success(`Transfer successful. Ref: ${res.data.reference}`);
      setShowTransfer(false);
      setTransferData({ to_account_id: '', amount: '', description: '' });
      handleSearch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    }
    setTransferLoading(false);
  };

  const handleDownloadStatement = async (accountId, accountNumber) => {
    try {
      const res = await api.get(`/savings-accounts/${accountId}/statement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement_${accountNumber}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (e) {
      toast.error('Failed to download statement');
    }
  };

  const handleDownloadLoanStatement = async (loanId, loanNumber) => {
    try {
      const res = await api.get(`/loans/${loanId}/statement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `LoanStatement_${loanNumber}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Loan statement downloaded');
    } catch (e) {
      toast.error('Failed to download loan statement');
    }
  };

  const handleDownloadCombinedStatement = async () => {
    if (!c) return;
    try {
      const res = await api.get(`/clients/${c.id}/statement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CombinedStatement_${c.client_number}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Combined statement downloaded');
    } catch (e) {
      toast.error('Failed to download combined statement');
    }
  };

  const c = result?.client;
  const accounts = result?.accounts || [];
  const loans = result?.loans || [];
  const transactions = result?.recent_transactions || [];

  return (
    <div className="space-y-6 fade-in" data-testid="client-lookup-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Dashboard</span><span>/</span><span className="text-[#1f1f1f] font-medium">Client Lookup</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">TRANSACTIONS</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Client Lookup</h1>
          <p className="text-sm text-gray-500 mt-1">Search by account number, ID number, name, or phone number</p>
        </div>
        {showNewClient && (
          <Button
            onClick={() => setShowNewClientModal(true)}
            className="bg-[#1f1f1f] hover:bg-[#333333] text-white"
            data-testid="new-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Client
          </Button>
        )}
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-3" data-testid="client-search-form">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter account number, ID number, name, or phone..."
            className="pl-12 py-6 text-base border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
            data-testid="lookup-search-input"
          />
        </div>
        <Button type="submit" disabled={searching} className="bg-[#1f1f1f] hover:bg-[#333333] px-8 py-6" data-testid="lookup-search-btn">
          {searching ? 'Searching...' : 'Search'}
        </Button>
      </form>

      {/* Multiple Results — Select One */}
      {multipleResults.length > 0 && (
        <div className="bg-white border border-gray-200 p-4" data-testid="multiple-results">
          <p className="text-sm font-medium text-[#1f1f1f] mb-3">Multiple clients found — select one:</p>
          <div className="space-y-2">
            {multipleResults.map(mc => (
              <button
                key={mc.id}
                onClick={() => selectClient(mc.client_number || mc.id_number)}
                className="w-full flex items-center gap-4 p-3 border border-gray-200 hover:bg-gray-50 text-left transition-colors"
                data-testid={`result-${mc.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#1f1f1f]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#1f1f1f]/50" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1f1f1f]">{mc.first_name} {mc.last_name}</p>
                  <p className="text-xs text-gray-500">{mc.client_number} | ID: {mc.id_number} | {mc.phone}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Single Client Result */}
      {c && (
        <div className="space-y-6" data-testid="client-result">
          {/* Client Profile Header */}
          <div className="bg-[#1f1f1f] text-white p-6 rounded-sm" data-testid="client-profile-header">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-white/60" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{c.first_name} {c.last_name}</h2>
                  <p className="text-sm text-gray-400">{c.client_number} | {c.id_number}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-300">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>
                    {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                    {c.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.address}, {c.city}</span>}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className={`text-xs px-2 py-1 rounded-full ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                  {c.status?.toUpperCase()}
                </span>
                {c.credit_score && (
                  <p className="text-xs text-gray-400 mt-2">Credit Score: <span className="text-[#C5A059] font-bold">{c.credit_score}</span></p>
                )}
                <Button size="sm" variant="outline" className="mt-2 text-xs border-white/30 text-white hover:bg-white/10" onClick={handleDownloadCombinedStatement} data-testid="combined-statement-btn">
                  <Download className="w-3.5 h-3.5 mr-1" />Combined Statement
                </Button>
              </div>
            </div>
          </div>

          <Tabs defaultValue="accounts">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="accounts">Accounts ({accounts.length})</TabsTrigger>
              <TabsTrigger value="loans">Loans ({loans.length})</TabsTrigger>
              <TabsTrigger value="transactions">Recent Transactions</TabsTrigger>
              <TabsTrigger value="info">Client Info</TabsTrigger>
            </TabsList>

            {/* Accounts Tab */}
            <TabsContent value="accounts" className="space-y-3 pt-4">
              {accounts.length === 0 ? (
                <p className="text-sm text-gray-500 py-8 text-center">No savings accounts</p>
              ) : accounts.map(acc => (
                <div key={acc.id} className="bg-white border border-gray-200 p-4" data-testid={`account-card-${acc.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#1f1f1f]">{acc.account_number}</p>
                      <p className="text-xs text-gray-500">{acc.product_name || 'Savings Account'} | Status: {acc.status}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1f1f1f]">{formatCurrency(acc.balance)}</p>
                      <p className="text-xs text-gray-400">Available: {formatCurrency(acc.available_balance)}</p>
                    </div>
                  </div>
                  {acc.status === 'active' && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => { setTxAccountId(acc.id); setShowTransaction('deposit'); }} data-testid={`deposit-${acc.id}`}>
                        <ArrowUpCircle className="w-3.5 h-3.5 mr-1" />Deposit
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => { setTxAccountId(acc.id); setShowTransaction('withdraw'); }} data-testid={`withdraw-${acc.id}`}>
                        <ArrowDownCircle className="w-3.5 h-3.5 mr-1" />Withdraw
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => openTransfer(acc.id)} data-testid={`transfer-${acc.id}`}>
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />Transfer
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => handleDownloadStatement(acc.id, acc.account_number)} data-testid={`statement-${acc.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" />Statement
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </TabsContent>

            {/* Loans Tab */}
            <TabsContent value="loans" className="space-y-3 pt-4">
              {loans.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 mb-3">No active loans</p>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/loans?action=new&client_id=${c.id}&client_name=${encodeURIComponent(c.first_name + ' ' + c.last_name)}`)} data-testid="new-loan-btn">
                    <FileText className="w-3.5 h-3.5 mr-1" />Create New Loan
                  </Button>
                </div>
              ) : loans.map(loan => (
                <div key={loan.id} className="bg-white border border-gray-200 p-4" data-testid={`loan-card-${loan.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#1f1f1f]">{loan.loan_number || loan.id}</p>
                      <p className="text-xs text-gray-500">{loan.product_name || 'Loan'} | {loan.status}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1f1f1f]">{formatCurrency(loan.principal_amount || loan.principal || loan.amount)}</p>
                      <p className="text-xs text-gray-400">Rate: {loan.interest_rate}% | Term: {loan.term_months}m</p>
                    </div>
                  </div>
                  {/* Credit Scores */}
                  <div className="flex gap-4 mt-2 text-xs">
                    {loan.account_credit_score != null && (
                      <span className="text-gray-500">Account Score: <span className="font-bold text-[#C5A059]" data-testid={`loan-acct-score-${loan.id}`}>{loan.account_credit_score}</span></span>
                    )}
                    {loan.group_credit_score != null && (
                      <span className="text-gray-500">Group Score: <span className="font-bold text-[#C5A059]" data-testid={`loan-group-score-${loan.id}`}>{loan.group_credit_score}</span></span>
                    )}
                    {loan.credit_score_at_application != null && (
                      <span className="text-gray-500">Client Score (at application): <span className="font-medium">{loan.credit_score_at_application}</span></span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap items-center">
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => handleDownloadLoanStatement(loan.id, loan.loan_number || loan.id)} data-testid={`loan-statement-${loan.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" />Statement
                    </Button>
                    {(user?.role === 'loan_officer' || user?.role === 'admin' || user?.role === 'manager') && (
                      <SpreadScoreEditor loanId={loan.id} currentScore={loan.account_credit_score} currentSpread={loan.credit_spread} onSaved={() => selectClient(c.id)} />
                    )}
                  </div>
                </div>
              ))}
              {loans.length > 0 && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/loans?action=new&client_id=${c.id}&client_name=${encodeURIComponent(c.first_name + ' ' + c.last_name)}`)} data-testid="new-loan-btn">
                  <FileText className="w-3.5 h-3.5 mr-1" />Create New Loan
                </Button>
              )}
            </TabsContent>

            {/* Recent Transactions Tab */}
            <TabsContent value="transactions" className="pt-4">
              {transactions.length === 0 ? (
                <p className="text-sm text-gray-500 py-8 text-center">No recent transactions</p>
              ) : (
                <div className="bg-white border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Type</th>
                        <th className="px-4 py-3 text-left">Description</th>
                        <th className="px-4 py-3 text-left">Reference</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions.map((t, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-xs">{formatDate(t.created_at)}</td>
                          <td className="px-4 py-2.5">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              t.amount > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                            }`}>
                              {(t.type || '').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{t.description || '-'}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-400">{t.reference || '-'}</td>
                          <td className={`px-4 py-2.5 text-xs text-right font-medium ${t.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {t.amount > 0 ? '+' : ''}{formatCurrency(t.amount)}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-right font-medium">{formatCurrency(t.balance_after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* Client Info Tab */}
            <TabsContent value="info" className="pt-4">
              <div className="bg-white border border-gray-200 p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Personal</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex gap-2"><CreditCard className="w-4 h-4 text-gray-400" /><span className="text-gray-500">ID:</span><span className="font-medium">{c.id_number}</span></div>
                      <div className="flex gap-2"><Phone className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Phone:</span><span className="font-medium">{c.phone}</span></div>
                      <div className="flex gap-2"><Mail className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Email:</span><span className="font-medium">{c.email || '-'}</span></div>
                      <div className="flex gap-2"><MapPin className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Address:</span><span className="font-medium">{[c.address, c.city, c.province, c.postal_code].filter(Boolean).join(', ') || '-'}</span></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Employment</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex gap-2"><Briefcase className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Occupation:</span><span className="font-medium">{c.occupation || '-'}</span></div>
                      <div className="flex gap-2"><Briefcase className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Employer:</span><span className="font-medium">{c.employer || '-'}</span></div>
                      <div className="flex gap-2"><Banknote className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Income:</span><span className="font-medium">{c.monthly_income ? formatCurrency(c.monthly_income) : '-'}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* No result message when nothing searched yet */}
      {!result && multipleResults.length === 0 && !searching && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Enter an account number, ID number, or client name to begin</p>
        </div>
      )}

      {/* Deposit/Withdraw Dialog */}
      <Dialog open={!!showTransaction} onOpenChange={() => setShowTransaction(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{showTransaction === 'deposit' ? 'Make Deposit' : 'Make Withdrawal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (ZAR)</Label>
              <Input type="number" value={txAmount} onChange={(e) => setTxAmount(e.target.value)} placeholder="0.00" data-testid="tx-amount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransaction(null)}>Cancel</Button>
            <Button onClick={() => handleTransaction(showTransaction)} disabled={txLoading}
              className={showTransaction === 'deposit' ? 'bg-emerald-600' : 'bg-red-600'} data-testid="confirm-tx-btn">
              {txLoading ? 'Processing...' : showTransaction === 'deposit' ? 'Deposit' : 'Withdraw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransfer} onOpenChange={() => setShowTransfer(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#C5A059]" />Internal Transfer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">To Account</Label>
              <Select value={transferData.to_account_id} onValueChange={(v) => setTransferData(p => ({...p, to_account_id: v}))}>
                <SelectTrigger data-testid="transfer-to-select">
                  <SelectValue placeholder="Select destination account" />
                </SelectTrigger>
                <SelectContent>
                  {allAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.account_number} - {a.client_name} ({formatCurrency(a.balance)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Amount (ZAR)</Label>
              <Input type="number" value={transferData.amount} onChange={(e) => setTransferData(p => ({...p, amount: e.target.value}))} placeholder="0.00" data-testid="transfer-amount-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
              <Input value={transferData.description} onChange={(e) => setTransferData(p => ({...p, description: e.target.value}))} placeholder="Reference" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(false)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={transferLoading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="confirm-transfer-btn">
              {transferLoading ? 'Processing...' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Client Modal — shared tabbed form (reused from admin view) */}
      <NewClientModal
        open={showNewClientModal}
        onClose={() => setShowNewClientModal(false)}
        onSuccess={() => {
          setShowNewClientModal(false);
          toast.success('Client created successfully');
        }}
      />
    </div>
  );
};

const SpreadScoreEditor = ({ loanId, currentScore, currentSpread, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [score, setScore] = useState(currentScore ?? '');
  const [spread, setSpread] = useState(currentSpread ?? '');

  const handleSave = async () => {
    try {
      const payload = {};
      if (score !== '' && score !== currentScore) payload.score = parseInt(score);
      if (spread !== '' && spread !== currentSpread) payload.credit_spread = parseFloat(spread);
      if (Object.keys(payload).length === 0) { setEditing(false); return; }
      await api.put(`/loans/${loanId}/credit-score`, payload);
      toast.success('Credit info updated');
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  if (!editing) {
    return (
      <Button size="sm" variant="ghost" className="text-xs text-gray-500 h-7 px-2" onClick={() => setEditing(true)} data-testid={`edit-credit-${loanId}`}>
        <CreditCard className="w-3 h-3 mr-1" />Edit Score/Spread
      </Button>
    );
  }

  return (
    <div className="flex gap-2 items-center" data-testid={`credit-editor-${loanId}`}>
      <Input type="number" placeholder="Score" value={score} onChange={e => setScore(e.target.value)} className="h-7 w-20 text-xs" />
      <Input type="number" step="0.1" placeholder="Spread %" value={spread} onChange={e => setSpread(e.target.value)} className="h-7 w-20 text-xs" />
      <Button size="sm" onClick={handleSave} className="h-7 text-xs bg-[#1f1f1f] text-white px-2">Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs px-2">Cancel</Button>
    </div>
  );
};

export default ClientLookupPage;
